import React, { useState, useEffect, useRef } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { jwtDecode } from 'jwt-decode';
import { GetStatisticsOfUsersAndAddressAsync, StatisticsData } from "../../Admin-Api/GetStatisticsOfUsersAndAddressAsync";
import Footer from "../Footer/Footer";
import { getServerInfoAsync, ServerInfo } from "../../Admin-Api/GetServerInfo";
import { FiChevronDown } from "react-icons/fi";
import packageJson from "../../../package.json";

interface CustomJwtPayload {
    RoleId: string;
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);
    const [showServerInfo, setShowServerInfo] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowServerInfo(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate('/');
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                if (decodedToken.RoleId !== "1") {
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate('/');
                }
            } catch (error) {
                toast.error("Invalid token. Please log in again.");
                navigate('/');
            }
        }
        const loadServerInfo = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const data = await getServerInfoAsync(token);
                setServerInfo(data);
            } catch (error) {
                console.error("Failed to load server info", error);
            }
        };

        loadServerInfo();
    }, [navigate]);

    const formatDate = (date: Date | null) => {
        if (!date) return "";
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    const fetchStatistics = async (fromDate: string, toDate: string) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No auth token found');

            const data = await GetStatisticsOfUsersAndAddressAsync(fromDate, toDate, token);
            setStatisticsData(data);
            if (data.usersCount === 0)
                if (!toast.isActive("no-users")) {
                    toast.info("No users were registered within the selected date range.", { toastId: "no-users" });
                }
        } catch (error) {
            // toast.error("An error occurred while fetching statistics.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFetchData = () => {
        if (!startDate || !endDate) {
            if (!toast.isActive("select-dates")) {
                toast.info("Please select both start and end date.", { toastId: "select-dates" });
            }
            return;
        }
        if (startDate > endDate) {
            if (!toast.isActive("invalid-dates")) {
                toast.error("Start date must be earlier than or equal to end date.", { toastId: "invalid-dates" });
            }
            return;
        }
        fetchStatistics(formatDate(startDate), formatDate(endDate));
    };

    return (

        <div className="h-screen overflow-hidden bg-magnolia">
            <div className="flex flex-col h-screen">
                <header className="bg-white px-4 sm:px-6 py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
                            <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
                            Dashboard
                        </h1>

                        {serverInfo && (
                            <div className="relative w-full lg:w-auto" ref={dropdownRef}>
                                <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm bg-white border rounded-lg px-3 sm:px-4 py-3 shadow-sm">
                                    <span className="font-medium">
                                        V {packageJson.version}
                                    </span>

                                    <span>|</span>
                                    
                                    <span className="font-medium">
                                        V {serverInfo.applicationVersion}
                                    </span>

                                    <span>|</span>

                                    <span className="break-words">
                                        {serverInfo.serverName}
                                    </span>

                                    <span>|</span>

                                    <span className="break-words">
                                        {serverInfo.pamsDatabaseVersion}
                                    </span>

                                    <button
                                        onClick={() => setShowServerInfo(!showServerInfo)}
                                        className="ml-auto flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                    >
                                        <svg
                                            className={`w-4 h-4 transition-transform duration-200 ${showServerInfo ? "rotate-180" : ""
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                {showServerInfo && (
                                    <div className="absolute right-0 lg:right-0 left-0 lg:left-auto top-full mt-2 z-50 w-full lg:w-[420px] bg-white border rounded-lg p-4 shadow-xl">
                                        <div className="grid grid-cols-1 gap-2 text-sm">
                                             <div>
                                                <strong>Frontend Version:</strong>{" "}
                                                {packageJson.version}
                                            </div>
                                            
                                            <div>
                                                <strong>Server Version:</strong>{" "}
                                                {serverInfo.applicationVersion}
                                            </div>

                                            <div>
                                                <strong>Server Name:</strong>{" "}
                                                {serverInfo.serverName}
                                            </div>

                                            <div>
                                                <strong>PAMS DB Version:</strong>{" "}
                                                {serverInfo.pamsDatabaseVersion}
                                            </div>

                                            <div>
                                                <strong>PBDP DB Version:</strong>{" "}
                                                {serverInfo.pbdpDatabaseVersion}
                                            </div>

                                            <div>
                                                <strong>Data Version:</strong>{" "}
                                                {serverInfo.dataVersion}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </header>
                <main className="flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="container px-4 sm:px-6 py-6 mx-auto">
                        <h4 className="text-base sm:text-lg text-dark font-semibold">
                            User and Address Statistics
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-4 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        setStartDate(date);

                                        if (endDate && date && endDate < date) {
                                            setEndDate(null);
                                        }
                                    }}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={new Date()}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="dd-mm-yyyy"
                                    isClearable
                                    className="border rounded p-2 w-full"
                                />

                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">End Date</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    maxDate={new Date()}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText="dd-mm-yyyy"
                                    disabled={!startDate}
                                    isClearable
                                    className="border rounded p-2 w-full"
                                />
                            </div>
                            <button
                                onClick={handleFetchData}
                                className="bg-indigo-600 text-white px-4 h-10 rounded-md self-start sm:self-end mt-2 sm:mt-0"
                            >
                                Fetch Data
                            </button>
                        </div>
                        {isLoading && <p className="mt-4 text-gray-500">Loading data...</p>}
                        {statisticsData && statisticsData.topAddressesByPincode && (
                            <div className="mt-6">

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                                    <div className="p-6 bg-white rounded-lg shadow-md">
                                        <h5 className="text-sm font-semibold text-gray-600">Registered Users ({formatDate(startDate)} to {formatDate(endDate)})</h5>
                                        <p className="text-2xl font-bold text-indigo-600">{statisticsData.usersCount}</p>
                                    </div>
                                    <div className="p-6 bg-white rounded-lg shadow-md">
                                        <h5 className="text-sm font-semibold text-gray-600">Added Addresses ({formatDate(startDate)} to {formatDate(endDate)})</h5>
                                        <p className="text-2xl font-bold text-indigo-600">{statisticsData.addressesCount}</p>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <div className="p-6 bg-white rounded-lg shadow-md">
                                        <h5 className="text-sm font-semibold text-gray-600">Top Pincodes</h5>
                                        <ul className="text-sm font-bold text-indigo-600 mt-2 max-h-[300px] overflow-y-auto list-none font-mono">
                                            {statisticsData.topAddressesByPincode.map((pincode, index) => (
                                                <li key={index}>
                                                    {pincode.pinCode} - {pincode.addressCount} addresses
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </main>

                <ToastContainer />
                <Footer />
            </div>

        </div>
    );

};

export default Dashboard;
